import { PartialRecord } from '@/shared/utils/typesUtils'
import { ComparisonOperator } from '@hello-aria/query-string'
import { isEmpty, isNil, isString } from 'lodash'
import {
    QueryParams,
    QueryParamsKey,
    QueryParamsNamespace,
    QueryParamsType,
    QueryParamsValue,
    SortingRule
} from '../models/queryModels'
import { usePathname, useRouter } from '../../navigation'
import { useSearchParams } from 'next/navigation'
import { ComparisonFilterInput, FilterParamsInput, QueryStringInput } from '@hello-aria/query-string/dist/stringify'
import {
    createParser,
    useQueryStates,
    ParserBuilder,
    useQueryState,
    parseAsString,
    parseAsArrayOf,
    parseAsStringLiteral
} from 'nuqs'
import { mapSearch, MapSearchInput, mapSortBy } from '../utils/queryUtils'
import { homeTableFields } from '@/modules/home/enums/homeEnums'
import { useCallback } from 'react'
import { receivablesTableFields } from '@/modules/receivables/enums/receivablesEnums'
import { invoiceTableFields } from '@/modules/invoices/enums/invoicesEnums'
import { creditsTableFields } from '@/modules/credits/enums/creditsEnums'
import { creditMemoTableFields } from '@/modules/credit-memo/enums/creditMemoEnums'
import { repaymentsTableFields } from '@/modules/repayments/enums/repaymentsEnums'
import { MultiValue, SingleValue } from 'react-select'
import { redirectionTableFields } from '@/modules/redirections/enums/redirectionEnums'
import { quotesTableFields } from '@/modules/quotes/enums/quotesEnums'
import { debtorsTableFields } from '../enums/debtorsEnums'
import { usersTableFields } from '../../modules/users/enums/usersEnums'
import { userLimitsTableFields } from '../../modules/user-limits/enums/userLimitsEnums'

export type ResetQueryParamState =
    | {
          key:
              | `${QueryParamsNamespace}_${Extract<QueryParamsType, 'sort' | 'search' | 'id' | 'activeTab'>}`
              | `${QueryParamsNamespace}_${Extract<QueryParamsType, 'sort' | 'search' | 'id' | 'activeTab'>}`[]
      }
    | {
          key: `${QueryParamsNamespace}_${Extract<QueryParamsType, 'filter'>}`
          filterPath: string[]
          filterOperator: ComparisonOperator
      }
    | {
          key: `${QueryParamsNamespace}_${Extract<QueryParamsType, 'filter'>}`[]
      }
    | { key: 'loanId' }

interface QueryParamState {
    getAll: () => QueryParams | undefined
    pick: <Paths extends QueryParamsKey[]>(paths: Paths) => QueryParams | undefined
    get: <T extends QueryParamsKey>(path: T) => QueryParams[T] | undefined
    set: (payload: PartialRecord<QueryParamsKey, QueryParamsValue>) => void
}

function parseIfNeeded(value: unknown) {
    if (typeof value === 'string') {
        try {
            const parsedValue = JSON.parse(value)
            if (typeof parsedValue === 'object' && (Array.isArray(parsedValue) || parsedValue !== null)) {
                return parsedValue
            }
        } catch (e) {
            return value
        }
    }
    return value
}

export const useQueryParamsState = (): QueryParamState => {
    const router = useRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const parsedQuery = searchParams ? Object.fromEntries(searchParams.entries()) : {}

    return {
        getAll: () => parsedQuery,
        get: (path) => {
            if (!parsedQuery) {
                return undefined
            }

            return parseIfNeeded(parsedQuery[path])
        },
        pick: (paths) => {
            if (!parsedQuery) {
                return undefined
            }

            return paths.reduce((acc, p) => ({ ...acc, [p]: parseIfNeeded(parsedQuery[p]) }), {})
        },
        set: (payload) => {
            const params = new URLSearchParams(searchParams?.toString())

            Object.entries(payload).forEach(([key, value]) => {
                if (isString(value) && !isEmpty(value)) {
                    params.set(key, value)
                    return
                }

                if (isComparisonFilterInput(value) && !isNil(value[0].value) && !isEmpty(value[0].value)) {
                    params.set(key, JSON.stringify(value))
                    return
                }

                if (!isComparisonFilterInput(value) && !isEmpty(value?.[0])) {
                    params.set(key, JSON.stringify(value))
                    return
                }

                params.delete(key)
            })

            const stringifiedParams = params.toString()

            return router.push(`${pathname}${stringifiedParams ? '?' : ''}${stringifiedParams}`, { scroll: false })
        }
    }
}

export const mutationModes = ['idle', 'create', 'update', 'delete'] as const
export type MutationMode = (typeof mutationModes)[number]

const isComparisonFilterInput = (
    value: string | null | ComparisonFilterInput[] | SortingRule<any>[]
): value is ComparisonFilterInput[] => {
    return !isNil(value?.[0]) && typeof value === 'object' && 'value' in value[0]
}

type ModeParserOptions<
    T extends readonly string[],
    P extends Record<string, ParserBuilder<any>> = Record<string, never>
> = {
    modes: T
    defaultMode: T[number]
    additionalParams?: P
}

type ExtractParserReturnType<T> = T extends ParserBuilder<infer R> ? R : never

type ModeState<T extends readonly string[], P> = {
    mode: T[number]
} & {
    [K in keyof P]: ExtractParserReturnType<P[K]>
}

type ModeStateUpdate<T extends readonly string[], P> =
    P extends Record<string, never>
        ? { mode?: T[number] | null }
        : { mode?: T[number] | null } & { [K in keyof P]?: ExtractParserReturnType<P[K]> | null }

/**
 * This util function is used to create a mode parser that can be used to parse and serialize mode state.
 * It's useful for components that have different modes and need to manage the state of the mode.
 * You can have additional params that you want to manage along with the mode state.
 * @param options
 * @returns {useModeState, isMode}
 */
export function createModeParser<
    T extends readonly string[],
    P extends Record<string, ParserBuilder<any>> = Record<string, never>
>(options: ModeParserOptions<T, P>) {
    const { modes, defaultMode, additionalParams = {} as P } = options

    function isMode(value: unknown): value is T[number] {
        return typeof value === 'string' && modes.includes(value as T[number])
    }

    const modeParser = createParser({
        parse: (value: string): T[number] => (isMode(value) ? value : defaultMode),
        serialize: (value: T[number]): string => value
    }).withDefault(defaultMode)

    function useModeState() {
        return useQueryStates({
            mode: modeParser,
            ...additionalParams
        }) as [ModeState<T, P>, (value: ModeStateUpdate<T, P> | null) => void]
    }

    return {
        useModeState,
        isMode
    }
}

export const QUERY_NAMESPACES = [
    'loans',
    'receivables',
    'invoices',
    'creditsLimits',
    'creditMemos',
    'repayments',
    'redirections',
    'quotes',
    'debtors',
    'users',
    'userLimits'
] as const
export type QueryNamespace = (typeof QUERY_NAMESPACES)[number]

const getSearchFields = (namespace: QueryNamespace) => {
    const mappedSearchFields: Record<QueryNamespace, MapSearchInput['path']> = {
        loans: homeTableFields,
        receivables: receivablesTableFields,
        invoices: invoiceTableFields,
        creditsLimits: creditsTableFields,
        creditMemos: creditMemoTableFields,
        repayments: repaymentsTableFields,
        redirections: redirectionTableFields,
        quotes: quotesTableFields,
        debtors: debtorsTableFields,
        users: usersTableFields,
        userLimits: userLimitsTableFields
    }

    return mappedSearchFields[namespace]
}

const buildQuery = ({
    namespace,
    initialFilters,
    search,
    sort
}: {
    namespace: QueryNamespace
    initialFilters?: FilterParamsInput[]
    search: string
    sort: SortingRule<any>[] | null
}): QueryStringInput => {
    const filters: FilterParamsInput[] = initialFilters || []

    const mappedSearch = mapSearch({
        path: getSearchFields(namespace),
        value: search
    })

    if (mappedSearch && !isEmpty(mappedSearch)) {
        filters.push(...mappedSearch)
    }

    return {
        filters,
        sorts: sort && !isEmpty(sort) ? mapSortBy(sort) : undefined
    }
}

export const useQueryBuilder = ({
    namespace,
    filters
}: {
    namespace: QueryNamespace
    filters?: FilterParamsInput[]
}) => {
    const [sort, setSort] = useQueryState(`${namespace}_sort`, {
        parse: (value: string) => JSON.parse(value) as SortingRule<any>[],
        serialize: (value: SortingRule<any>[]) => JSON.stringify(value)
    })

    const [search, setSearch] = useQueryState(`${namespace}_search`, parseAsString.withDefault(''))

    const handleSort = (sortBy: SortingRule<any>[]) => {
        setSort(sortBy)
    }

    const handleSearch = useCallback(
        (value: string) => {
            setSearch(value)
        },
        [setSearch]
    )

    const query = buildQuery({
        namespace,
        initialFilters: filters,
        search,
        sort
    })

    return {
        query,
        sort,
        search,
        handleSort,
        handleSearch
    }
}

type FilterType = 'single' | 'multi'

export type Filter<T extends string> = {
    name: string
    type: FilterType
    items: readonly T[]
    value: T[] | null
    onChange: (selected: SingleValue<{ value: T; label: string }> | MultiValue<{ value: T; label: string }>) => void
    getLabel: (item: T) => string
}

type FiltersInput<T extends Record<string, readonly string[]>> = {
    [K in keyof T]: {
        type: FilterType
        items: T[K]
        getLabel: (item: T[K][number]) => string
    }
}

type FiltersOutput<T extends Record<string, readonly string[]>> = {
    [K in keyof T]: Filter<T[K][number]>
}

function useFilter<T extends string>(namespace: QueryNamespace, name: string, items: readonly T[]) {
    const key = `${namespace}_${name}`
    return useQueryState(key, parseAsArrayOf(parseAsStringLiteral(items)))
}

export function useFiltersBuilder<T extends Record<string, readonly string[]>>(
    namespace: QueryNamespace,
    config: FiltersInput<T>
): FiltersOutput<T> {
    const result = {} as FiltersOutput<T>

    for (const key in config) {
        const { type, items, getLabel } = config[key]
        type Item = T[typeof key][number]
        const [value, setValue] = useFilter<Item>(namespace, key, items)

        result[key] = {
            type,
            items,
            value,
            name: key,
            getLabel,
            onChange: (
                selected:
                    | SingleValue<{
                          value: Item
                          label: string
                      }>
                    | MultiValue<{ value: Item; label: string }>
            ) => {
                if (type === 'single' && selected && 'value' in selected) {
                    setValue([selected.value])
                    return
                }

                if (type === 'multi' && Array.isArray(selected)) {
                    setValue(selected.map((s) => s.value))
                    return
                }

                setValue(null)
            }
        }
    }

    return result
}
